import React, { useState, ReactElement } from "react";
import { NextPageContext } from "next";
import Link from "next/link";
import { useSWRInfinite } from "swr";
import EvergreenFilterBar from "components/cards/EvergreenFilterBar";
import Nav from "components/Nav";
import Spinner from "components/Spinner";
import CardFooter from "components/cards/CardFooter";
import EvergreenCard from "components/cards/EvergreenCard";
import { request, EvergreensListAPIResponse } from "endpoints";
import withAuth from "../../withAuth";
import * as types from "../../types";

interface Props {
  keyword: string | null;
}

interface QueryParams {
  keyword?: string;
  interval?: string;
  entryPoint?: boolean;
}

const fetcher = (url: string): Promise<EvergreensListAPIResponse> =>
  request<EvergreensListAPIResponse>(url);

function EvergreenCardsList({ keyword }: Props): React.ReactNode {
  // For filtering
  const [dateSelected, dateSetterRaw] = useState<string | null>(() => {
    const dateFromCache: string = sessionStorage.getItem("notesFilters:date");
    return dateFromCache ? dateFromCache : null;
  });
  const dateSetter = (val: string | null): void => {
    dateSetterRaw(val);
    sessionStorage.setItem("notesFilters:date", val);
  };
  const [otherSelected, otherSetterRaw] = useState<string | null>(() => {
    const otherFromCache: string = sessionStorage.getItem("notesFilters:other");
    return otherFromCache ? otherFromCache : null;
  });
  const otherSetter = (val: string | null): void => {
    otherSetterRaw(val);
    sessionStorage.setItem("notesFilters:other", val);
  };
  const filterProps = {
    dateSelected,
    dateSetter,
    otherSelected,
    otherSetter,
  };

  const makeUrl = (): string => {
    const queryParams = new URLSearchParams();
    if (keyword) {
      queryParams.append("keyword", keyword);
    }
    if (dateSelected) {
      queryParams.append("interval", dateSelected);
    }
    if (otherSelected === "entryPoint") {
      queryParams.append("entryPoint", "true");
    }
    let url = "/evergreens/";
    if (queryParams.toString() !== "") {
      url = url + "?" + queryParams.toString();
    }
    return url;
  };

  // SWR
  const getKey = (pageIndex: number, prevPageData: EvergreensListAPIResponse): string | null => {
    if (prevPageData && !prevPageData.next) return null;
    if (pageIndex === 0) return makeUrl();
    return prevPageData.next.replace(/^.*\/\/[^/]+/, "");
  };

  const { data, error, size, setSize } = useSWRInfinite<EvergreensListAPIResponse>(getKey, fetcher);

  const cards: types.EvergreenCard[] = [];
  if (data) {
    data.forEach(page => {
      if (page) {
        page.results.forEach(card => {
          cards.push(card);
        });
      }
    });
  }

  const loadMoreCards = async (): Promise<void> => {
    setSize(size + 1);
  };

  const isLoading = !data && !error;
  const isLoadingMore = isLoading || (size > 0 && data && typeof data[size - 1] === "undefined");
  // const isEmpty = data ? data[0] ? data[0].results.length === 0 | ;
  const isMore = (): boolean => {
    if (data && data[data.length - 1] && data[data.length - 1].next !== null) return true;
    return false;
  };

  return (
    <div>
      <Nav />
      <EvergreenFilterBar {...filterProps} />
      {keyword ? <h2 className="text-lg pt-2">Keyword: {keyword}</h2> : null}
      {isLoading ? <Spinner /> : <EvergreenListItems cards={cards} />}
      {cards.length > 0 ? (
        <MoreEntriesButton isMore={isMore()} isLoading={isLoadingMore} loadMore={loadMoreCards} />
      ) : null}
    </div>
  );
}

interface EvergreenListItemsProps {
  cards: types.EvergreenCard[];
}

const EvergreenListItems: React.FC<EvergreenListItemsProps> = ({ cards }): ReactElement => {
  if (cards.length === 0) {
    return <div className="p-2 text-center">No cards</div>;
  } else {
    const items = cards.map(card => {
      return (
        <li key={card.id}>
          <Link href="/notes/[id]" as={`/notes/${card.id}`} prefetch={false}>
            <div className="card-item">
              <EvergreenCard card={card} />
              <CardFooter
                cardType="evergreen"
                id={card.id}
                keywords={card.keywords}
                created={card.created}
              />
            </div>
          </Link>
        </li>
      );
    });
    return <ul>{items}</ul>;
  }
};

interface MoreEntriesButtonProps {
  isMore: boolean;
  isLoading: boolean;
  loadMore: () => void;
}

const MoreEntriesButton: React.FC<MoreEntriesButtonProps> = ({ isMore, isLoading, loadMore }) => {
  const btnClass = "bg-gray-400 hover:bg-gray-600 text-white font-bold py-2 px-4 rounded";
  if (!isMore) {
    return null;
  } else if (isLoading) {
    return (
      <div className="text-center">
        <button className={btnClass} disabled={true}>
          Loading...
        </button>
      </div>
    );
  } else {
    return (
      <div className="text-center">
        <button className={btnClass} onClick={loadMore}>
          Load more
        </button>
      </div>
    );
  }
};

interface InitialProps {
  keyword: string | string[] | null;
}

EvergreenCardsList.getInitialProps = ({ query: { keyword } }: NextPageContext): InitialProps => {
  return { keyword };
};

export default withAuth(EvergreenCardsList);
